import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import useQueryGetPlayerList from '@/modules/manage-player/api/hooks/useQueryGetPlayerList'
import GetPlayerListListing from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListListing'
import { useState } from 'react'

import type { ExportPlayerListCsvRequest } from '@/modules/manage-player/type'

type SortOrder = 'asc' | 'desc' | undefined

function GetPlayerListPage() {
	const {
		pageSize,
		currentPage,
		searchQuery,
		rankFilter,
		bannedFilter,
		channelFilter,
	} = useTablePagination()

	const [mainWalletBalanceSort, setMainWalletBalanceSort] =
		useState<SortOrder>(undefined)
	const [orderByCreatedAt, setOrderByCreatedAt] = useState<SortOrder>('desc')
	const [promotionWalletBalanceSort, setPromotionWalletBalanceSort] =
		useState<SortOrder>(undefined)
	const handleSort = (columnId: string, order: SortOrder) => {
		if (columnId === 'mainWallet_balance') {
			setMainWalletBalanceSort(order)
			setOrderByCreatedAt(undefined)
			setPromotionWalletBalanceSort(undefined)
		} else if (columnId === 'createdAt') {
			setOrderByCreatedAt(order)
			setMainWalletBalanceSort(undefined)
			setPromotionWalletBalanceSort(undefined)
		} else if (columnId === 'promotionWallet_balance') {
			setPromotionWalletBalanceSort(order)
			setOrderByCreatedAt(undefined)
			setMainWalletBalanceSort(undefined)
		}
	}

	const payload = {
		take: pageSize,
		skip: (currentPage - 1) * pageSize,
		search: searchQuery,
		rankId: rankFilter || undefined,
		channelName: channelFilter || undefined,
		...(bannedFilter === 'true' || bannedFilter === 'false'
			? { isBanned: bannedFilter === 'true' }
			: {}),
		sortByMainWalletBalance: mainWalletBalanceSort,
		orderByCreatedAt,
		sortByPromotionWalletBalance: promotionWalletBalanceSort,
	}

	const { data, isLoading } = useQueryGetPlayerList(payload)

	const exportParams = {
		search: searchQuery,
		rankId: rankFilter || undefined,
		channelName: channelFilter || undefined,
		...(bannedFilter === 'true' || bannedFilter === 'false'
			? { isBanned: bannedFilter === 'true' }
			: {}),
		...(mainWalletBalanceSort && {
			sortByMainWalletBalance: mainWalletBalanceSort as 'asc' | 'desc',
		}),
		...(orderByCreatedAt && {
			orderByCreatedAt: orderByCreatedAt as 'asc' | 'desc',
		}),
		...(promotionWalletBalanceSort && {
			sortByPromotionWalletBalance: promotionWalletBalanceSort as
				| 'asc'
				| 'desc',
		}),
	} as Omit<
		ExportPlayerListCsvRequest,
		'take' | 'skip' | 'filename' | 'columnMappings'
	>

	return (
		<PageContainer>
			<GetPlayerListListing
				data={data?.data || []}
				total={data?.total || 0}
				isLoading={isLoading}
				onSort={handleSort}
				exportParams={exportParams}
			/>
		</PageContainer>
	)
}

const GuardedGetPlayerListPage = withUserPermission(GetPlayerListPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_PLAYER],
})

export default GuardedGetPlayerListPage
